// Paints the scene from scene.js across three stacked canvases. The phone draws
// the same data with Skia; the layer order here mirrors that renderer: sky
// gradient, ambient dust, earned stars, the blurred aurora curtains, their
// crowns, the horizon glow, then either a floor vignette (portrait) or a seeded
// ridgeline the aurora rises from behind (landscape), and finally the shooting
// stars.
//
// The aurora is softened by a blur, and the only reliable way to blur it on iOS
// Safari is a CSS `filter` on a DOM element, because a 2D context's own `filter`
// property is ignored there and the curtains would render as hard-edged bars. A
// CSS blur applies to a whole element, and the aurora sits between the sharp
// stars below it and the sharp glow, ridge, and shooting stars above it, so it
// gets its own middle canvas. The back canvas holds the stars, the front canvas
// holds everything drawn over the aurora, the middle canvas holds only the
// curtains and carries the blur, and a fourth grain canvas on top blends film
// grain over the finished picture. Only the curtains' opacity and the shooting
// stars move per frame alongside the protocol stars. The activity stars bake
// once onto the back canvas, the glow bakes
// once into an offscreen sprite the front canvas blits, and the grain bakes once.
import { paintProtocolStars, placeProtocolStars } from "./protocol_stars.js";
import { hashSeed, mulberry32 } from "./random.js";
import { curtainBlur, buildScene, veilGradientStops } from "./scene.js";
import { buildShootingStars } from "./shooting_stars.js";

const TAU = Math.PI * 2;
const PULSE_PERIOD_MS = 3500;
const PULSE_DEPTH = 0.25;
// The CSS blur that softens the aurora also spreads its light thinner than Skia's
// blur does on the phone, so the same beams read a touch dimmer in the browser.
// Brightening the blurred layer lifts the beams back toward the app without
// touching the empty sky, since a fully transparent pixel has no color to brighten.
const VEIL_BRIGHTNESS = 1.18;
// A tile of tight, near-neutral-gray noise stands in for the phone's fractal
// grain, so the large soft washes do not read as flat gradients. It rides on its
// own top canvas under CSS `mix-blend-mode: overlay`, which blends against the
// finished sky the way the phone's overlay grain does. Painting it with the
// canvas's own overlay operator instead would blend against that layer's empty
// transparent pixels and lift the blacks to gray, so the DOM blend is the only
// way to grain the whole picture without muddying it. The values sit in a tight
// band around neutral gray (128) so the grain stays texture rather than static;
// how far it reads is the grain canvas's CSS opacity, not a value here.
const GRAIN_TILE = 256;
const GRAIN_MIN = 112;
const GRAIN_RANGE = 32;
// Star size is calibrated to a phone-like width so a wide desktop canvas does not
// scale the field into boulders. It is a CSS width, multiplied by the device
// pixel ratio where it is used, so a star renders at the same real size on a phone
// and on a low-density desktop monitor alike. Star positions still fill the whole
// canvas.
const REFERENCE_CSS_WIDTH = 380;
// The aurora is a centered band, not the full canvas width, because a handful of
// beams only read as a striped aurora when they stay packed. Its width tracks the
// viewport height so it rises full-height and stays a prominent column on desktop,
// never narrower than a phone and never wider than the canvas.
const CURTAIN_HEIGHT_FRACTION = 0.72;
// On a wide (landscape) screen the aurora rises from behind a seeded ridgeline
// that spans the full width, so the empty sides become sky over land rather than
// dead space. Portrait screens (phones) keep the floor vignette and no ridge, so
// the mobile view is untouched. The two ridges layer for depth, the far one a
// touch higher and lighter than the near one.
const HORIZON_MIN_ASPECT = 1.15;
const RIDGE_FAR = { base: 0.85, amp: 0.045, steps: 64 };
const RIDGE_NEAR = { base: 0.93, amp: 0.06, steps: 72 };
const SHOOT_DURATION_MS = 850;
const HEAD_SIZE = 5;
const HEAD_GLOW_RADIUS = 8;

function offscreen(width, height) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function paintStars(ctx, stars, starRgb) {
  for (const star of stars) {
    ctx.fillStyle = `rgba(${starRgb},${star.alpha.toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.r, 0, TAU);
    ctx.fill();
  }
}

function paintBase(ctx, scene, palette, width, height) {
  const sky = ctx.createLinearGradient(0, 0, 0, height);
  sky.addColorStop(0, palette.sky.top);
  sky.addColorStop(0.55, palette.sky.mid);
  sky.addColorStop(1, palette.sky.bottom);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, width, height);
  paintStars(ctx, scene.ambientStars, palette.star);
  paintStars(ctx, scene.stars, palette.star);
}

function paintGrain(ctx, seed, width, height) {
  const grain = makeGrain(ctx, seed);
  if (!grain) return;
  ctx.fillStyle = grain;
  ctx.fillRect(0, 0, width, height);
}

function ridgeLine(seed, key, width, height, spec) {
  const rng = mulberry32(hashSeed(`${seed}-ridge-${key}`));
  const amp = spec.amp * height;
  const baseY = height * spec.base;
  const f1 = 1.3 + rng();
  const f2 = 2.7 + rng() * 2;
  const f3 = 5 + rng() * 3;
  const p1 = rng() * TAU;
  const p2 = rng() * TAU;
  const p3 = rng() * TAU;
  const points = [];
  for (let i = 0; i <= spec.steps; i++) {
    const u = i / spec.steps;
    const undulation =
      Math.sin(u * f1 * TAU + p1) * 0.6 + Math.sin(u * f2 * TAU + p2) * 0.3 + Math.sin(u * f3 * TAU + p3) * 0.14;
    points.push([u * width, baseY - undulation * amp]);
  }
  return points;
}

function buildRidges(seed, width, height) {
  return {
    far: ridgeLine(seed, "far", width, height, RIDGE_FAR),
    near: ridgeLine(seed, "near", width, height, RIDGE_NEAR),
  };
}

function fillRidge(ctx, points, width, height, fill, rim) {
  ctx.beginPath();
  ctx.moveTo(0, height);
  for (const [x, y] of points) ctx.lineTo(x, y);
  ctx.lineTo(width, height);
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();

  ctx.beginPath();
  points.forEach(([x, y], i) => (i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  ctx.strokeStyle = rim;
  ctx.lineWidth = 1.2;
  ctx.stroke();
}

function bakeGlow(scene, palette, width, height, curtainWidth, ridges) {
  const canvas = offscreen(width, height);
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;

  for (const crown of scene.crowns) {
    ctx.fillStyle = `rgba(${palette.glow},${crown.alpha.toFixed(2)})`;
    ctx.beginPath();
    ctx.arc(crown.x, crown.y, crown.r, 0, TAU);
    ctx.fill();
  }

  const glow = ctx.createRadialGradient(
    width * 0.5,
    scene.horizonY,
    0,
    width * 0.5,
    scene.horizonY,
    curtainWidth * 0.42
  );
  glow.addColorStop(0, `rgba(${palette.glow},${scene.glowAlpha.toFixed(3)})`);
  glow.addColorStop(1, `rgba(${palette.glow},0)`);
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);

  if (ridges) {
    // A soft bloom hugging the horizon, then the ridgeline itself, which occludes
    // the bright bases of the beams so they read as an aurora rising over land.
    const band = ctx.createLinearGradient(0, height * 0.55, 0, height * 0.82);
    band.addColorStop(0, `rgba(${palette.glow},0)`);
    band.addColorStop(1, `rgba(${palette.glow},0.1)`);
    ctx.fillStyle = band;
    ctx.fillRect(0, height * 0.5, width, height * 0.35);
    // Near-black ridge fills in the floor's blue-black tone; the far ridge sits a
    // touch lighter than the near one for depth.
    fillRidge(ctx, ridges.far, width, height, "rgba(13,17,27,1)", `rgba(${palette.glow},0.14)`);
    fillRidge(ctx, ridges.near, width, height, "rgba(5,7,13,1)", `rgba(${palette.glow},0.09)`);
  } else {
    const floor = ctx.createLinearGradient(0, height * 0.72, 0, height);
    floor.addColorStop(0, `rgba(${palette.floor},0)`);
    floor.addColorStop(0.55, `rgba(${palette.floor},0.18)`);
    floor.addColorStop(1, `rgba(${palette.floor},1)`);
    ctx.fillStyle = floor;
    ctx.fillRect(0, 0, width, height);
  }

  return canvas;
}

function makeGrain(ctx, seed) {
  const tile = offscreen(GRAIN_TILE, GRAIN_TILE);
  const tileCtx = tile.getContext("2d");
  if (!tileCtx) return null;
  const image = tileCtx.createImageData(GRAIN_TILE, GRAIN_TILE);
  const rng = mulberry32(hashSeed(`${seed}-grain`));
  for (let i = 0; i < image.data.length; i += 4) {
    const value = GRAIN_MIN + Math.floor(rng() * GRAIN_RANGE);
    image.data[i] = value;
    image.data[i + 1] = value;
    image.data[i + 2] = value;
    image.data[i + 3] = 255;
  }
  tileCtx.putImageData(image, 0, 0);
  return ctx.createPattern(tile, "repeat");
}

export function createSkyRenderer({ back, veil, front, grain }) {
  const backCtx = back.getContext("2d");
  const veilCtx = veil.getContext("2d");
  const frontCtx = front.getContext("2d");
  const grainCtx = grain.getContext("2d");
  let s = null;
  let rafId = null;
  let startedAt = null;

  function build(config) {
    // iOS Safari returns a null 2D context once the page's total canvas memory is
    // spent, so any layer's context can come back null on a tight device. When one
    // does, decline to paint and leave the aside's gradient showing rather than
    // dereference null and throw.
    if (!backCtx || !veilCtx || !frontCtx || !grainCtx) {
      s = null;
      return;
    }
    const { seed, beams, activityCount, palette, tenureWeeks, width, height, dpr, still } = config;
    const referenceWidth = Math.min(width, REFERENCE_CSS_WIDTH * dpr);
    const landscape = width > height * HORIZON_MIN_ASPECT;
    const curtainWidth = landscape
      ? Math.min(width, Math.max(referenceWidth * 1.15, height))
      : Math.min(width, Math.max(referenceWidth, height * CURTAIN_HEIGHT_FRACTION));
    const ridges = landscape ? buildRidges(seed, width, height) : null;
    const starInsets = config.starInsets ?? { top: 120, bottom: 100 };
    const scene = buildScene(
      beams,
      seed,
      width,
      height,
      palette,
      activityCount,
      referenceWidth,
      curtainWidth,
      config.stars ? 0.75 : 1,
      config.stars ? { seed, top: (starInsets.top + 32) * dpr } : undefined
    );
    const ridgeTop = ridges ? Math.min(...ridges.far.map((point) => point[1])) / dpr : height / dpr;
    const stars = placeProtocolStars(config.stars ?? [], {
      seed,
      width: width / dpr,
      height: height / dpr,
      insets: { top: starInsets.top, bottom: Math.max(starInsets.bottom, height / dpr - ridgeTop + 20) },
    });

    const glow = bakeGlow(scene, palette, width, height, curtainWidth, ridges);
    if (!glow) {
      s = null;
      return;
    }

    const veilGradients = scene.veils.map((curtain) => {
      const { colors, positions } = veilGradientStops(curtain);
      const gradient = veilCtx.createLinearGradient(0, curtain.topY, 0, curtain.bottomY);
      positions.forEach((position, i) => gradient.addColorStop(Math.max(0, Math.min(1, position)), colors[i]));
      return gradient;
    });

    s = {
      scene,
      stars,
      palette,
      width,
      height,
      dpr,
      still,
      glow,
      ridgeTop: ridgeTop * dpr,
      veilGradients,
      shooting: buildShootingStars(seed, tenureWeeks, width, height),
    };

    paintBase(backCtx, scene, palette, width, height);
    paintGrain(grainCtx, seed, width, height);
    // The blur is calibrated in canvas backing-store pixels but CSS applies it in
    // layout pixels, so it divides by the device pixel ratio the canvas is scaled by.
    const blurCssPx = curtainBlur(beams.length, curtainWidth, palette) / dpr;
    veil.style.filter = `blur(${blurCssPx}px) brightness(${VEIL_BRIGHTNESS})`;
  }

  function paintShooting(elapsedMs) {
    for (const star of s.shooting) {
      const t = (elapsedMs - star.delay) / SHOOT_DURATION_MS;
      if (t <= 0 || t >= 1) continue;
      const p = 1 - (1 - t) * (1 - t);
      let opacity;
      if (p < 0.2) opacity = p / 0.2;
      else if (p > 0.8) opacity = (1 - p) / 0.2;
      else opacity = 1;

      const headX = star.startX + star.dx * p;
      const headY = star.startY + star.dy * p;
      const distance = Math.hypot(star.dx, star.dy);
      const tailX = headX - (star.dx / distance) * star.length;
      const tailY = headY - (star.dy / distance) * star.length;

      const trail = frontCtx.createLinearGradient(tailX, tailY, headX, headY);
      trail.addColorStop(0, "rgba(255,246,228,0)");
      trail.addColorStop(1, `rgba(255,246,228,${(0.9 * opacity).toFixed(3)})`);
      frontCtx.strokeStyle = trail;
      frontCtx.lineWidth = 2 * s.dpr;
      frontCtx.lineCap = "round";
      frontCtx.beginPath();
      frontCtx.moveTo(tailX, tailY);
      frontCtx.lineTo(headX, headY);
      frontCtx.stroke();

      const radius = HEAD_GLOW_RADIUS * s.dpr;
      const halo = frontCtx.createRadialGradient(headX, headY, 0, headX, headY, radius);
      halo.addColorStop(0, `rgba(255,233,201,${(0.8 * opacity).toFixed(3)})`);
      halo.addColorStop(0.4, `rgba(255,233,201,${(0.28 * opacity).toFixed(3)})`);
      halo.addColorStop(1, "rgba(255,233,201,0)");
      frontCtx.fillStyle = halo;
      frontCtx.beginPath();
      frontCtx.arc(headX, headY, radius, 0, TAU);
      frontCtx.fill();

      frontCtx.fillStyle = `rgba(255,250,240,${opacity.toFixed(3)})`;
      frontCtx.beginPath();
      frontCtx.arc(headX, headY, (HEAD_SIZE / 2) * s.dpr, 0, TAU);
      frontCtx.fill();
    }
  }

  function frame(elapsedMs) {
    veilCtx.clearRect(0, 0, s.width, s.height);
    const clock = elapsedMs / PULSE_PERIOD_MS;
    s.scene.veils.forEach((curtain, i) => {
      veilCtx.globalAlpha = s.still ? 1 : 1 - PULSE_DEPTH + PULSE_DEPTH * Math.sin((clock + i * 0.618) * TAU);
      veilCtx.fillStyle = s.veilGradients[i];
      veilCtx.fillRect(
        curtain.x - curtain.halfWidth,
        curtain.topY,
        curtain.halfWidth * 2,
        curtain.bottomY - curtain.topY
      );
    });
    veilCtx.globalAlpha = 1;

    frontCtx.clearRect(0, 0, s.width, s.height);
    frontCtx.drawImage(s.glow, 0, 0);
    paintProtocolStars(frontCtx, s.stars, s.palette, elapsedMs, s.still, s.dpr);
    frontCtx.save();
    frontCtx.beginPath();
    frontCtx.rect(0, 0, s.width, s.ridgeTop);
    frontCtx.clip();
    paintStars(frontCtx, s.scene.nearStars, s.palette.star);
    frontCtx.restore();
    if (!s.still) paintShooting(elapsedMs);
  }

  function loop(now) {
    if (startedAt === null) startedAt = now;
    frame(now - startedAt);
    rafId = requestAnimationFrame(loop);
  }

  function start() {
    stop();
    if (!s) return;
    if (s.still) {
      frame(0);
      return;
    }
    startedAt = null;
    rafId = requestAnimationFrame(loop);
  }

  function stop() {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  }

  return { build, start, stop };
}
